import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { UserCog } from 'lucide-react'

import { columns } from './components/users-columns'
import { UsersDialogs } from './components/users-dialogs'
import { UsersPrimaryButtons } from './components/users-primary-buttons'
import UsersProvider from './context/users-context'
import { User, userListSchema } from './data/schema'
import { useTranslation } from 'react-i18next'
import { get_users } from '@/api/user'
import { useEffect, useState } from 'react'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { useAuth } from '@/stores/authStore'
import { DataTable } from '@/components/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { filterNonNullValues } from '@/lib/utils'
import { FilterConfig } from '@/components/data-table/types'
import { cabinet_role } from '@/i18n'
import { Role } from './data/schema'

export interface UsersTableParams {
  page: number
  page_size: number
  ordering: string | null
  name: string | null
  is_active: string[] | null,
  roles: string[] | null
}

export default function Users() {
  const is_admin = useAuth().user?.is_admin || false
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter();
  const search = useSearch({ strict: false });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [pagesCnt, setPagesCnt] = useState<number>(0);
  const [params, setParams] = useState<UsersTableParams>({
    page: search?.page || 0,
    page_size: search?.page_size || 10,
    ordering: search?.ordering || null,
    name: search?.name || null,
    is_active: search?.is_active ?? ['true'],
    roles: search?.roles ?? null,
  });

  const handleRowClick = (row: User) => {
    navigate({
      to: `/users/${row.id}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { userData: row, timestamp: Date.now() } as any
    })
  };

  // Parse user list
  useEffect(() => {
    get_users(params).then((resp) => {
      const users_list = userListSchema.parse(resp.results)
      setUsers(users_list)
      setPagesCnt(Math.ceil(resp.count / params.page_size))
      setLoading(false)
      setError(false)
    }).catch((_) => {
      setError(true)
      setLoading(false)
    });
  }, [params]);

  // Sync params to router search params
  useEffect(() => {
    router.navigate({
      to: ".",
      search: filterNonNullValues({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
        name: params.name,
        is_active: params.is_active,
        roles: params.roles,
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const filterConfig: FilterConfig = {
    fields: [
      {
        id: 'name',
        label: t('search_by_name'),
        type: 'search'
      },
      {
        id: 'is_active',
        label: t('status'),
        type: 'select',
        options: [
          { label: t('status_active'), value: 'true' },
          { label: t('status_locked'), value: 'false' },
        ]
      },
      {
        id: 'roles',
        label: t('role'),
        type: 'select',
        options: cabinet_role.map((t: Role) => ({ label: t.name, value: t.id.toString() }))
      }
    ]
  }

  return (
    <UsersProvider>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <UserCog className="h-5 w-5" />
            </span>
          }
          title={t('users_list')}
          description={is_admin ? t('users_list_desc') : undefined}
          actions={loading ? <Skeleton className="h-9 w-32" /> : <UsersPrimaryButtons />}
        />
        <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
          {
            loading ? <DataTableSkeleton columns={5} rows={5} />
              : error ?
                <div className='py-8 text-center'>
                  {t('table_load_error')}
                </div>
                : <DataTable
                  data={users}
                  columns={is_admin ? columns : columns.slice(0, columns.length - 1)}
                  params={params}
                  setParams={setParams}
                  pages={pagesCnt}
                  filterConfig={filterConfig}
                  onRowClick={handleRowClick}
                  storageKey="users-table"
                />
          }
        </div>
      </Main>

      <UsersDialogs params={params} setParams={setParams} />
    </UsersProvider>
  )
}
